import {
  createReapproCategory,
  updateReapproCategory,
  useProducts,
  useReapproCategories,
  useReapproCategory,
} from '@gozoki/api'
import {
  useReapproCategoryForm,
  type ReapproCategoryForm,
} from '@gozoki/api/src/forms/useReapproCategoryForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, XIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useController } from 'react-hook-form'
import { Field } from '~/components/new/app-ui/form-fields/field'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { TimePicker } from '~/components/ui/time-picker'
import { useRights } from '../../../utils/hooks'
import { ProductDualListPanel } from '../../inputs/ProductDualListPanel'

interface ReapproCategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reapproCategoryId?: number
  onSuccess?: () => void
}

export const ReapproCategoryDialog = ({
  open,
  onOpenChange,
  reapproCategoryId,
  onSuccess,
}: ReapproCategoryDialogProps) => {
  const isUserAdmin = useRights(['ADMIN'])
  const queryClient = useQueryClient()
  const updateMode = reapproCategoryId !== undefined

  const { data } = useReapproCategory(reapproCategoryId)
  const { data: products } = useProducts()
  const { data: reapproCategories } = useReapproCategories()

  const { handleSubmit, control, setValue, reset } = useReapproCategoryForm({})

  const { field: productIdsField } = useController({ control, name: 'productIds' })

  const selectedProductIds = Array.isArray(productIdsField.value) ? productIdsField.value : []
  const activeProducts = useMemo(
    () => (products ?? []).filter((product) => product.isActive),
    [products],
  )

  const reapproCategoryLabelById = useMemo(
    () =>
      (reapproCategories ?? []).reduce(
        (acc, category) => {
          acc[category.id] = category.name
          return acc
        },
        {} as Record<number, string>,
      ),
    [reapproCategories],
  )

  const productItems = useMemo(
    () =>
      activeProducts.map((product) => {
        const groupId = product.reapproCategoryId ?? 0
        return {
          id: product.id,
          label: product.label,
          groupId,
          groupLabel: reapproCategoryLabelById[groupId] ?? 'AUTRE',
        }
      }),
    [activeProducts, reapproCategoryLabelById],
  )

  useEffect(() => {
    if (!open || updateMode) return
    reset()
  }, [open, updateMode, reset])

  useEffect(() => {
    if (!data) return
    setValue('name', data.name)
    setValue('daysBefore', data.daysBefore)
    setValue('coverageRate', data.coverageRate)
    setValue('computeHour', data.computeHour.slice(0, 5))
    setValue('sendHour', data.sendHour.slice(0, 5))
    setValue('logHour', data.logHour.slice(0, 5))
    setValue('productIds', data.products)
    setValue('isDispatch', data.isDispatch)
  }, [data, setValue])

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] })
    queryClient.invalidateQueries({ queryKey: ['reapproCategories'] })
    onSuccess?.()
    onOpenChange(false)
  }

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: createReapproCategory,
    onSuccess: handleSuccess,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: (formData: ReapproCategoryForm) =>
      updateReapproCategory(reapproCategoryId ?? 0, formData),
    onSuccess: handleSuccess,
  })

  const onSubmit = (formData: ReapproCategoryForm) => {
    if (updateMode) {
      update(formData)
    } else {
      create(formData)
    }
  }

  const isPending = isCreating || isUpdating

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-225 max-w-[calc(100%-2rem)] flex flex-col p-0 gap-0"
        showCloseButton={false}
      >
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
          <DialogTitle className="text-lg font-medium">
            {updateMode
              ? isUserAdmin
                ? 'Modifier la catégorie de réappro'
                : 'Voir la catégorie de réappro'
              : 'Ajouter une catégorie de réappro'}
          </DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" />}>
            <XIcon />
            <span className="sr-only">Fermer</span>
          </DialogClose>
        </div>

        <form
          id="reappro-category-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-6"
        >
          {/* Paramètres de réappro */}
          <div className="bg-muted/40 rounded-xl p-5 border border-border flex flex-col gap-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Settings size={16} className="text-primary" />
              Paramètres de réappro
            </h3>

            <Field label="Nom catégorie">
              <Controller
                name="name"
                control={control}
                render={({ field }) => <Input {...field} disabled={!isUserAdmin} />}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="J-X">
                <Controller
                  name="daysBefore"
                  control={control}
                  render={({ field }) => <Input {...field} disabled={!isUserAdmin} />}
                />
              </Field>
              <Field label="Taux de couverture">
                <Controller
                  name="coverageRate"
                  control={control}
                  render={({ field }) => <Input {...field} disabled={!isUserAdmin} />}
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Heure calcul">
                <Controller
                  name="computeHour"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      onChange={(value) => {
                        if (!isUserAdmin) return
                        field.onChange(value)
                      }}
                    />
                  )}
                />
              </Field>
              <Field label="Heure envoi">
                <Controller
                  name="sendHour"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      onChange={(value) => {
                        if (!isUserAdmin) return
                        field.onChange(value)
                      }}
                    />
                  )}
                />
              </Field>
              <Field label="Heure log">
                <Controller
                  name="logHour"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      onChange={(value) => {
                        if (!isUserAdmin) return
                        field.onChange(value)
                      }}
                    />
                  )}
                />
              </Field>
            </div>

            <Controller
              name="isDispatch"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={!isUserAdmin}
                    className="accent-primary cursor-pointer"
                  />
                  <span className="text-sm">Besoin de dispatch</span>
                </label>
              )}
            />

            <p className="text-xs text-muted-foreground italic bg-background rounded-lg px-3 py-2 border border-border/50">
              Les paramètres définis au niveau produit écrasent ceux de la catégorie.
            </p>
          </div>

          <ProductDualListPanel
            productItems={productItems}
            categoryId={reapproCategoryId}
            value={selectedProductIds}
            onChange={productIdsField.onChange}
            disabled={!isUserAdmin}
          />
        </form>

        <DialogFooter className="shrink-0 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
            Annuler
          </Button>
          {isUserAdmin && (
            <Button type="submit" form="reappro-category-form" disabled={isPending}>
              Enregistrer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
